// ============================================================
// navModel — the pure part of the Section Navigator (CLB-080 S3): the rows a
// portal's section menu shows (label · one-line description · live count),
// the type-to-filter, and the "Recent" list (last 3 sections per portal and
// world, kept in this browser only — localStorage behind try/catch).
// ============================================================
import { SECTION_DESC, ORDERS_WORLDS } from './portalMeta';

export const FILTER_FROM = 7; // a filter box appears above six rows
export const RECENT_MAX = 3;
const LS = 'clb.nav.recent.v1';

export function describe(portalKey, worldKey, s) {
  if (s.d) return s.d;
  if (portalKey === 'orders') {
    const w = ORDERS_WORLDS.find((x) => x.key === worldKey);
    const hit = w && w.sections.find((x) => x.key === s.key);
    return (hit && hit.d) || '';
  }
  return SECTION_DESC[`${portalKey}.${s.key}`] || '';
}

export function buildRows(portalKey, worldKey, sections) {
  return (sections || []).map((s) => ({ ...s, d: describe(portalKey, worldKey, s) }));
}

export function filterRows(rows, q) {
  const t = String(q || '').trim().toLowerCase();
  if (!t) return rows;
  return rows.filter((r) => `${r.label} ${r.d || ''}`.toLowerCase().includes(t));
}

function store() {
  const v = JSON.parse(window.localStorage.getItem(LS) || '{}');
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

const scope = (portalKey, worldKey) => `${portalKey}/${worldKey || ''}`;

export function readRecents(portalKey, worldKey) {
  try {
    const all = store();
    const v = all[scope(portalKey, worldKey)];
    return Array.isArray(v) ? v.slice(0, RECENT_MAX) : [];
  } catch (e) { return []; }
}

export function pushRecent(portalKey, worldKey, key) {
  try {
    const all = store();
    const k = scope(portalKey, worldKey);
    const prev = Array.isArray(all[k]) ? all[k] : [];
    all[k] = [key, ...prev.filter((x) => x !== key)].slice(0, RECENT_MAX);
    window.localStorage.setItem(LS, JSON.stringify(all));
  } catch (e) { /* storage unavailable — Recent just stays empty */ }
}

// Recent = the last sections visited, other than the current one, that still exist.
export function recentRows(rows, recents, currentKey) {
  return recents.filter((k) => k !== currentKey).map((k) => rows.find((r) => r.key === k)).filter(Boolean);
}

// ---- Home (CLB-080 S5): the section a portal opens on, pinned per portal in this browser ----
// Only portals whose entry is a single section state take a Home (Orders opens by world; QC and
// Projects have no section menu). The stored key is checked against the live sections on use.
export const HOME_PORTALS = ['developments', 'sales', 'catalog'];
const HOME_LS = 'clb.nav.home.v1';

export function readHomes() {
  try {
    const v = JSON.parse(window.localStorage.getItem(HOME_LS) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (e) { return {}; }
}

export function readHome(portalKey) { return readHomes()[portalKey] || null; }

/** Pin `key` as the portal's Home; pinning the current Home again clears it. Returns the new Home or null. */
export function toggleHome(portalKey, key) {
  const all = readHomes();
  if (all[portalKey] === key) delete all[portalKey]; else all[portalKey] = key;
  try { window.localStorage.setItem(HOME_LS, JSON.stringify(all)); } catch (e) { /* storage unavailable — the pin just does not stick */ }
  return all[portalKey] || null;
}

/** The Home section to open when entering a portal, or null (none pinned, or the pinned section is gone). */
export function homeFor(portalKey, validKeys) {
  if (!HOME_PORTALS.includes(portalKey)) return null;
  const h = readHome(portalKey);
  return h && (validKeys || []).includes(h) ? h : null;
}
